import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { resetPassword } from "@/lib/auth";
import { humanizeError } from "@/lib/errorMessages";
import { validatePassword, validateMatch, PASSWORD_MIN_LENGTH } from "@/lib/validation";
import { useAuthModal } from "@/components/auth/AuthModalProvider";
import { Button, Card, PasswordInput, Field } from "@/components/ui";
import { BrandLogoMark, useBrandName } from "@/brand";
import { buildHead } from "@/lib/seo";

// Public landing for the password-reset magic link. Reads ?token, lets the
// user set a new password, then (redirect-to-login) sends them to sign in
// with it — no session is minted here.
export const Route = createFileRoute("/reset-password")({
  head: () => buildHead({ path: "/reset-password", title: "Reset your password", noindex: true }),
  validateSearch: (s: Record<string, unknown>): { token?: string } => ({
    token: typeof s.token === "string" ? s.token : undefined,
  }),
  component: ResetPasswordPage,
});

type Phase = "form" | "success" | "missing";

function ResetPasswordPage() {
  const { token } = Route.useSearch();
  const { t } = useTranslation("auth");
  const brand = useBrandName();
  const navigate = useNavigate();
  const auth = useAuthModal();
  const [phase, setPhase] = useState<Phase>(token ? "form" : "missing");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  // Inline per-field errors; `err` is reserved for the server's response.
  const [pwErr, setPwErr] = useState<string | null>(null);
  const [confirmErr, setConfirmErr] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    if (!token || busy) return;
    const pe = validatePassword(pw, { min: PASSWORD_MIN_LENGTH, emptyMessage: t("reset.emptyPassword") });
    const ce = !confirm ? t("reset.confirmRequired") : validateMatch(confirm, pw, t("reset.mismatch"));
    setPwErr(pe);
    setConfirmErr(ce);
    setErr(null);
    if (pe || ce) return;
    setBusy(true);
    try {
      await resetPassword(token, pw);
      setPhase("success");
    } catch (e) {
      setErr(humanizeError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-4">
      <Card className="w-full max-w-sm p-6">
        <div className="space-y-4">
          <div className="flex items-center justify-center gap-1.5 text-fg">
            <BrandLogoMark className="h-5 w-5 text-primary" />
            <span className="font-semibold">{brand}</span>
          </div>

          {phase === "form" && (
            <form
              className="space-y-3"
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                void submit();
              }}
            >
              <h1 className="text-center text-lg font-semibold text-fg">{t("reset.title")}</h1>
              <Field
                label={t("reset.newPassword")}
                htmlFor="rp-pw"
                error={pwErr ?? undefined}
                hint={pwErr ? undefined : t("fields.passwordHint", { count: PASSWORD_MIN_LENGTH })}
              >
                <PasswordInput
                  id="rp-pw"
                  autoComplete="new-password"
                  value={pw}
                  onChange={(e) => {
                    setPw(e.target.value);
                    if (pwErr) setPwErr(null);
                  }}
                  placeholder="••••••••"
                  invalid={!!pwErr}
                  required
                />
              </Field>
              <Field label={t("reset.confirmPassword")} htmlFor="rp-confirm" error={confirmErr ?? undefined}>
                <PasswordInput
                  id="rp-confirm"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => {
                    setConfirm(e.target.value);
                    if (confirmErr) setConfirmErr(null);
                  }}
                  placeholder="••••••••"
                  invalid={!!confirmErr}
                  required
                />
              </Field>
              {err && <p className="text-sm text-danger">{err}</p>}
              <Button type="submit" block loading={busy}>
                {t("reset.submit")}
              </Button>
            </form>
          )}

          {phase === "success" && (
            <div className="space-y-3 text-center">
              <h1 className="text-lg font-semibold text-fg">{t("reset.successTitle")}</h1>
              <p className="text-sm text-fg-muted">{t("reset.successBody")}</p>
              <Button
                block
                onClick={() => {
                  void navigate({ to: "/" });
                  auth.open({ mode: "login" });
                }}
              >
                {t("tabs.signIn")}
              </Button>
            </div>
          )}

          {phase === "missing" && (
            <div className="space-y-3 text-center">
              <h1 className="text-lg font-semibold text-fg">{t("reset.missingTitle")}</h1>
              <p className="text-sm text-fg-muted">{t("reset.missingBody")}</p>
              <Button block variant="outline" onClick={() => navigate({ to: "/" })}>
                {t("confirm.goHome")}
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
